import i18next, { t } from 'i18next'

import {
  IconButton,
  IconCaretLeft,
  IconCaretRight,
  Skeleton
} from '@vtex/shoreline'

function formatDayShort(iso: string): string {
  if (!iso) return ''
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  const locale = i18next.language || 'pt-BR'
  const monthAbbr = new Intl.DateTimeFormat(locale, {
    month: 'short'
  }).format(date)
  return `${day} de ${monthAbbr}`
}

function getDaysInRange(startDate: string, endDate: string): string[] {
  const days: string[] = []
  const cur = new Date(`${startDate}T00:00:00`)
  const end = new Date(`${endDate}T00:00:00`)
  while (cur <= end) {
    days.push(cur.toISOString().split('T')[0])
    cur.setDate(cur.getDate() + 1)
  }
  return days
}

export interface DailyFilterProps {
  loading: boolean
  selectedDay: string
  onDayChange: (day: string) => void
  dateRange: { start_date: string; end_date: string }
  campaignStartAt?: string
}

const DailyFilter = ({
  loading,
  selectedDay,
  onDayChange,
  dateRange,
  campaignStartAt
}: DailyFilterProps) => {
  const effectiveStart =
    campaignStartAt && campaignStartAt > dateRange.start_date
      ? campaignStartAt
      : dateRange.start_date
  const days = getDaysInRange(effectiveStart, dateRange.end_date)
  const idx = days.indexOf(selectedDay)
  const canPrev = idx > 0
  const canNext = idx < days.length - 1
  const dayLabel = (() => {
    const today = new Date()
    const fmt = (d: Date) => d.toISOString().split('T')[0]
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    const tomorrow = new Date(today)
    tomorrow.setDate(today.getDate() + 1)
    if (selectedDay === fmt(today)) return t('common:words.today')
    if (selectedDay === fmt(yesterday)) return t('common:words.yesterday')
    if (selectedDay === fmt(tomorrow)) return t('common:words.tomorrow')
    return formatDayShort(selectedDay)
  })()

  // height matches Shoreline's --sl-element-height (2.25rem = 36px) to prevent layout shift
  const wrapperStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '2.25rem'
  }

  if (loading) {
    return (
      <div style={wrapperStyle}>
        <Skeleton width="160px" height="2.25rem" />
      </div>
    )
  }

  return (
    <div style={wrapperStyle}>
      <IconButton
        label={t('rm:spendReport.dailyFilter.prevDay')}
        variant="tertiary"
        disabled={!canPrev}
        onClick={() => canPrev && onDayChange(days[idx - 1])}
      >
        <IconCaretLeft />
      </IconButton>
      <span
        style={{
          fontSize: '14px',
          minWidth: '80px',
          textAlign: 'center'
        }}
      >
        {dayLabel}
      </span>
      <IconButton
        label={t('rm:spendReport.dailyFilter.nextDay')}
        variant="tertiary"
        disabled={!canNext}
        onClick={() => canNext && onDayChange(days[idx + 1])}
      >
        <IconCaretRight />
      </IconButton>
    </div>
  )
}

export default DailyFilter
